import styles from './index.module.scss';
import { tiny_height, utility } from '@phi/components';
import {
    defineComponent,
    h,
    nextTick,
    onUnmounted,
    type PropType,
    reactive,
    ref,
    watch,
} from 'vue';
import { isEscape } from '@frontend/keyboard';
import { px } from '@phi/util/css';
import { next_frame } from '@vaaas/fpts/promise';
import { clamp } from '@vaaas/fpts/maths';
import { Text } from '@components/text';

export type Width =
    'normal'
    | 'narrow'
    | 'narrower'
    | 'extra-narrow'
    | 'wide'
    | 'wider';

export const Modal = defineComponent({
    props: {
        id: { required: false, type: String },

        title: { required: false, type: String, default: undefined },

        width: { required: false, type: String as PropType<Width>, default: 'normal' },

        backdrop: { required: false, type: Boolean, default: true },

        show: { required: true, type: Boolean },

        dismissible: { required: false, type: Boolean, default: true },

        draggable: { required: false, type: Boolean, default: true },

        unload: { required: false, type: Boolean, default: true },

        style: {
            required: false,
            type: Object as PropType<Record<string, string>>,
            default: () => ({}),
        },
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    setup(props, { emit, slots }) {
        const modal = ref(undefined as undefined | HTMLElement);
        const body = ref(undefined as undefined | HTMLElement);
        const hidden = ref(!props.show);
        const faded = ref(!props.show);
        const position = reactive({ left: 0, top: 0 });

        function start_drag() {
            document.addEventListener('mouseup', stop_drag);
            document.addEventListener('mousemove', drag);
        }

        function stop_drag() {
            document.removeEventListener('mouseup', stop_drag);
            document.removeEventListener('mousemove', drag);
        }

        function drag(e: MouseEvent) {
            if (!modal.value) return;
            const rect = modal.value.getBoundingClientRect();
            position.left += clamp(
                -rect.left,
                window.innerWidth - rect.right,
            )(e.movementX);
            position.top += clamp(
                -rect.top,
                window.innerHeight - rect.bottom,
            )(e.movementY);
        }

        function close() {
            emit('update:show', false);
        }

        function on_document_click(e: PointerEvent) {
            if (modal.value && e.target instanceof Node && !modal.value.contains(e.target))
                close();
        }

        function on_document_keyup(e: KeyboardEvent) {
            if (isEscape(e) && !(e.target instanceof HTMLInputElement))
                close();
        }

        function clean_up() {
            stop_drag();
            document.removeEventListener('pointerdown', on_document_click);
            document.removeEventListener('keyup', on_document_keyup);
        }

        onUnmounted(() => {
            clean_up();
        });

        watch(() => props.show, async (value) => {
            if (value) {
                position.left = 0;
                position.top = 0;
                hidden.value = !value;
                faded.value = value;
                await nextTick();
                await next_frame();
                await next_frame();
                faded.value = !props.show;

                if (props.dismissible) {
                    document.addEventListener('pointerdown', on_document_click);
                    document.addEventListener('keyup', on_document_keyup);
                }
            } else {
                faded.value = !value;
                setTimeout(() => {
                    hidden.value = !props.show;
                    if (body.value) body.value.scrollTop = 0;
                }, 250);
                clean_up();
            }
        });

        return () => props.unload && hidden.value ? '' :  h('div', {
            id: props.id,
            class: {
                [utility.absolute]: true,
                [utility.entire_screen]: true,
                [utility.hidden]: hidden.value,
                [styles.backdrop]: true,
                [utility.important_opaque]: faded.value,
                [utility.no_background]: !props.backdrop,
                [utility.no_interact]: !props.backdrop,
                [utility.flex]: true,
                [utility.align_center]: true,
                [utility.justify_center]: true,
                [utility.z_maximum]: true,
                [utility.unselectable]: true,
            },
        }, h('div', {
            class: {
                [styles.wrapper]: true,
                [styles.small]: faded.value,
                [styles.narrow]: props.width === 'narrow',
                [styles.narrower]: props.width === 'narrower',
                [styles.extra_narrow]: props.width === 'extra-narrow',
                [styles.wider]: props.width === 'wider',
            },
            style: props.style,
        }, h('div', {
            ref: modal,
            class: [
                utility.do_interact_with_children,
                utility.relative,
                utility.round,
                styles.modal,
            ],
            style: {
                left: px(position.left),
                top: px(position.top),
            },
        }, [
            props.title && h(Text, {
                class: { [utility.grab]: props.draggable },
                size: tiny_height() ? 'inherit' : 'big',
                weight: 'bold',
                as: 'header',
                onMousedown: props.draggable && start_drag,
            }, () => props.title),
            slots.body && h('div', {
                ref: body,
                class: [
                    styles.body,
                    utility.overflow,
                ],
            }, slots.body()),
            slots.footer && h('footer', {
                class: [
                    utility.right,
                    utility.inline_margin_gap_2,
                    styles.footer,
                ],
            }, slots.footer()),
        ])));
    },
});
